<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import { store } from '@/store/appStore.js'

const activeSku = ref(null)
const products  = computed(() => store.data.products.value)
const stockBySku = computed(() => { const m={}; store.data.stock.value.forEach(s=>{m[s.productSku]=(m[s.productSku]||0)+s.availableQty}); return m })
const currentSku = computed(() => activeSku.value || products.value.find(p=>p.isDisrupted)?.sku || products.value[0]?.sku)
const activeProduct = computed(() => products.value.find(p=>p.sku===currentSku.value))
const trend = computed(() => {
  const cur = stockBySku.value[currentSku.value] || 0
  const shape = activeProduct.value?.isDisrupted ? 'declining' : activeProduct.value?.isPromoted ? 'rising' : 'flat'
  const mk = (start, pct) => { const s=[]; let v=start; for(let m=0;m<4;m++){s.push(Math.round(v));v=v*(1+pct/100)} return s }
  return { months:['Apr','May','Jun','Jul'], series: shape==='declining'?mk(cur*2.6,-30):shape==='rising'?mk(cur*0.55,18):mk(cur*1.05,-1) }
})
const chartData = computed(() => ({ labels:trend.value.months, datasets:[{ label:'Available Qty (all warehouses)', data:trend.value.series, borderColor:activeProduct.value?.isDisrupted?'#C1392B':activeProduct.value?.isPromoted?'#3A9B5C':'#0F3D3E', backgroundColor:'rgba(15,61,62,0.06)', tension:0.3, fill:true, pointRadius:3 }] }))

// BRD Module 14 specifies five display fields: Product Name, Batch Number,
// Expiry Date, Available Quantity, and Stock Movement Trend. Only the product
// name and trend chart were rendered — batchNo, expiryDate, mfgDate and
// availableQty all existed in the stock data but were never surfaced.
const batchRows = computed(() =>
  store.data.stock.value
    .filter(s => s.productSku === currentSku.value)
    .sort((a, b) => a.warehouse.localeCompare(b.warehouse))
)
const totalAvailable = computed(() => batchRows.value.reduce((sum, s) => sum + s.availableQty, 0))

// Expiry proximity drives write-off risk, so flag it rather than printing a
// bare date the reviewer has to mentally diff against today.
const TODAY = '2026-07-29'
function monthsToExpiry(dateStr) {
  const [y, m] = dateStr.split('-').map(Number)
  const [ty, tm] = TODAY.split('-').map(Number)
  return (y - ty) * 12 + (m - tm)
}
function expiryTone(dateStr) {
  const months = monthsToExpiry(dateStr)
  if (months <= 3) return { cls: 'red', label: 'Near expiry' }
  if (months <= 9) return { cls: 'yellow', label: 'Monitor' }
  return { cls: 'green', label: 'OK' }
}
</script>
<template>
  <div>
    <div class="card module-header"><h2>Stock movement trend</h2></div>
    <div class="split-sidebar">
      <div class="card" style="max-height:520px;overflow-y:auto;">
        <div v-for="p in products" :key="p.sku" class="nav-item" style="color:var(--color-text);border-left-color:transparent;" :class="{ active: currentSku===p.sku }" @click="activeSku=p.sku">{{ p.productName }}</div>
      </div>
      <div class="card" style="padding:18px 20px;">
        <div class="eyebrow" style="margin-bottom:4px;">{{ activeProduct?.productName }} ({{ currentSku }})</div>
        <div class="muted" style="font-size:12.5px;margin-bottom:10px;">Stock movement across Harare Central, Bulawayo Depot, Mutare Sub-depot</div>
        <ChartView type="line" :labels="chartData.labels" :datasets="chartData.datasets" :height="280" :key="'avail-'+currentSku" />

        <div class="eyebrow" style="margin:22px 0 8px;">Batch Detail — {{ totalAvailable.toLocaleString() }} units available across {{ batchRows.length }} warehouse position{{ batchRows.length===1?'':'s' }}</div>
        <table class="data-table">
          <thead>
            <tr><th>Warehouse</th><th>Batch No.</th><th>Mfg Date</th><th>Expiry Date</th><th>Available</th><th>Reserved</th><th>In-Transit</th><th>Expiry Risk</th></tr>
          </thead>
          <tbody>
            <tr v-for="s in batchRows" :key="s.productSku + s.warehouse">
              <td class="cell-strong">{{ s.warehouse }}</td>
              <td class="cell-muted">{{ s.batchNo }}</td>
              <td class="cell-muted">{{ s.mfgDate }}</td>
              <td class="cell-muted">{{ s.expiryDate }}</td>
              <td :style="s.oosStatus ? 'color:var(--status-red);font-weight:700;' : ''">{{ s.availableQty }}</td>
              <td class="cell-muted">{{ s.reservedQty }}</td>
              <td class="cell-muted">{{ s.inTransitQty }}</td>
              <td><span class="status-pill" :class="expiryTone(s.expiryDate).cls"><span class="dot"></span>{{ expiryTone(s.expiryDate).label }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
